import * as React from 'react';
import { connect } from 'react-redux';

import Bone from '@lightspeed/cirrus-bone';
import Modal, { ModalFooter, ModalHeader } from '@lightspeed/cirrus-modal';
import Text from '@lightspeed/cirrus-text';
import { Alert } from '@lightspeed/flame/Alert';
import { Button } from '@lightspeed/flame/Button';
import ShopsAutocomplete from 'Autocomplete/Shops';
import VendorsAutocomplete from 'Autocomplete/Vendors';
import FormField from 'cirrus/FormField';
import { Form, withFormik } from 'formik';
import PropTypes from 'prop-types';
import qs from 'qs';

import { clearData, clearError, getData, postData, putData } from 'pages/store/actions/data';
import { actionTypes } from 'pages/store/actions/defaults';
import { endpoints, entities } from 'pages/store/schema';
import { getErrors, getProcessing, getValues } from 'pages/store/selectors';

import { formatOptions, VENDOR_RETURN_PATH } from './utils';

const hasEditVendorRight = () => window.merchantos.session.hasRight('vendors');

export class VendorReturnModal extends React.Component {
  componentDidUpdate(prevProps) {
    const { isVendorReturnSaving, savingVendorReturnErrors, setSubmitting, getApiData, resetForm } =
      this.props;

    if (prevProps.isVendorReturnSaving && !isVendorReturnSaving && !!savingVendorReturnErrors) {
      setSubmitting(false);
    }
    if (!prevProps.isOpen && this.props.isOpen) {
      resetForm();

      getApiData(endpoints.adminShop.all);
      getApiData(endpoints.vendor.all);
    }

    if ((prevProps.isOpen && !this.props.isOpen) || (!prevProps.isOpen && this.props.isOpen)) {
      this.props.clearError();
    }
  }

  componentWillUnmount() {
    this.props.clearData();
  }

  render() {
    const {
      shopID,
      vendorID,
      isOpen,
      isDisabled,
      handleCloseModal,
      isEditMode,
      isCreateMode,
      vendors = [],
      shops = [],
      loadingVendorErrors,
      loadingShopsErrors,
      savingVendorErrors,
      savingVendorReturnErrors,
      isVendorSaving,
      isLoading,
      setFieldValue,
      setFieldError,
      handleSubmit,
      isSubmitting,
      values,
      errors,
      warningMessage,
    } = this.props;
    const shopOptions = formatOptions(shops, 'shopID');
    const vendorOptions = formatOptions(vendors, 'vendorID');
    const shopValue =
      values.shopID && shopOptions
        ? // eslint-disable-next-line eqeqeq
          shopOptions.find(({ value }) => value == values.shopID)
        : null;
    const vendorValue =
      values.vendorID && vendorOptions
        ? // eslint-disable-next-line eqeqeq
          vendorOptions.find(({ value }) => value == values.vendorID)
        : null;
    const hasErrors =
      loadingVendorErrors || loadingShopsErrors || savingVendorErrors || savingVendorReturnErrors;
    const isPristine = shopID === values.shopID && vendorID === values.vendorID;

    return (
      <Modal isOpen={isOpen} onRequestClose={handleCloseModal}>
        <Form className="cirrus">
          <ModalHeader onCloseClicked={handleCloseModal}>
            <Text bodyLarge>
              {isCreateMode ? 'New vendor return' : null}
              {isEditMode ? 'Edit vendor return' : null}
            </Text>
          </ModalHeader>
          <div className="cr-modal__body" style={{ overflow: 'visible', maxWidth: '468px' }}>
            {warningMessage ? (
              <Alert type="warning" noCloseBtn data-test="vendor-return-modal-warning">
                <Text>{warningMessage}</Text>
              </Alert>
            ) : null}
            {hasErrors ? (
              <Alert type="danger" noCloseBtn>
                <>
                  {loadingVendorErrors && (
                    <Text>There was an error while loading the vendors.</Text>
                  )}
                  {loadingShopsErrors && <Text>There was an error while loading the shops.</Text>}
                  {savingVendorErrors && <Text>There was an error while adding the vendor.</Text>}
                  {savingVendorReturnErrors && (
                    <Text>
                      {`There was an error while ${
                        isCreateMode ? 'creating' : 'updating'
                      } the vendor return.`}
                    </Text>
                  )}
                </>
              </Alert>
            ) : null}
            <div
              style={{ width: '420px' }}
              className="cirrus cr-flex cr-width-full cr-justify-between cr-flex-col cr-ph-4 cr-mv-3"
            >
              {!isLoading && (
                <FormField
                  label="Vendor"
                  className="cr-mb-2"
                  status={errors.vendorID ? { type: 'error', message: errors.vendorID } : {}}
                >
                  <VendorsAutocomplete
                    name="vendorID"
                    defaultValue={vendorValue}
                    isError={!!errors.vendorID}
                    isLoading={isVendorSaving}
                    isCreatable={hasEditVendorRight()}
                    isClearable={false}
                    hasNone
                    isDisabled={isDisabled}
                    onChange={({ value } = {}) => {
                      setFieldValue('vendorID', value);
                      setFieldError('vendorID');
                    }}
                  />
                </FormField>
              )}
              {isLoading && <Bone width="20%" className="cr-mb-2" />}
              {isLoading && <Bone width="100%" className="cr-mb-2" height="1.875rem" />}

              {!isLoading && (
                <FormField
                  label="Shop"
                  status={errors.shopID ? { type: 'error', message: errors.shopID } : {}}
                >
                  <div data-test="vendor-return-modal-shop-dropdown">
                    <ShopsAutocomplete
                      name="shopID"
                      defaultValue={shopValue}
                      defaultOptions={shopOptions}
                      isError={!!errors.shopID}
                      isCreatable={false}
                      isClearable={false}
                      isDisabled={isDisabled || shopOptions.length === 1}
                      onChange={({ value } = {}) => {
                        setFieldValue('shopID', value);
                        setFieldError('shopID');
                      }}
                    />
                  </div>
                </FormField>
              )}
              {isLoading && <Bone width="20%" className="cr-mb-2" />}
              {isLoading && <Bone className="cr-mb-2" height="1.875rem" />}
            </div>
          </div>
          <ModalFooter>
            <div className="cr-flex cr-justify-end">
              <div className="cr-mr-2">
                <Button
                  fill
                  onClick={handleCloseModal}
                  disabled={isLoading || isSubmitting}
                  data-test="vendor-return-modal-cancel-button"
                >
                  Cancel
                </Button>
              </div>
              <Button
                variant="primary"
                fill
                type="submit"
                onClick={handleSubmit}
                loading={isSubmitting}
                disabled={isDisabled || isLoading || isSubmitting || isPristine}
                data-test="vendor-return-modal-save-button"
              >
                <span>
                  {isCreateMode ? 'Create vendor return' : null}
                  {isEditMode ? 'Save' : null}
                </span>
              </Button>
            </div>
          </ModalFooter>
        </Form>
      </Modal>
    );
  }
}

const mapStateToProps = (state) => ({
  vendors: getValues(entities.vendor)(state),
  shops: getValues(entities.adminShop)(state),
  isLoading:
    (getProcessing({ entity: entities.vendor, list: true, actionType: actionTypes.LOADING })(
      state
    ) ||
      getProcessing({ entity: entities.adminShop, list: true, actionType: actionTypes.LOADING })(
        state
      )) === undefined,
  isVendorSaving: getProcessing({
    entity: entities.vendor,
    list: false,
    actionType: actionTypes.SAVING,
  })(state),
  isVendorReturnSaving: getProcessing({
    entity: entities.vendorReturns,
    list: false,
    actionType: actionTypes.SAVING,
  })(state),
  loadingVendorErrors: getErrors({
    entity: entities.vendor,
    list: true,
    actionType: actionTypes.LOADING,
  })(state),
  loadingShopErrors: getErrors({
    entity: entities.adminShop,
    list: true,
    actionType: actionTypes.LOADING,
  })(state),
  savingVendorErrors: getErrors({
    entity: entities.vendor,
    list: false,
    actionType: actionTypes.SAVING,
  })(state),
  savingVendorReturnErrors: getErrors({
    entity: entities.vendorReturns,
    list: false,
    actionType: actionTypes.SAVING,
  })(state),
});

const mapDispatchToProps = (dispatch) => ({
  getApiData: ({ url, entity, list }) => dispatch(getData({ url, entity, list })),
  postDataVendor: ({ payload, onSuccess }) =>
    dispatch(postData(endpoints.vendor.create({ payload, onSuccess }))),
  postDataVendorReturns: ({ payload, onSuccess }) =>
    dispatch(postData(endpoints.vendorReturns.create({ payload, onSuccess }))),
  putDataVendorReturns: ({ id, payload, onSuccess }) =>
    dispatch(putData(endpoints.vendorReturns.edit({ id, payload, onSuccess }))),
  clearData: () => dispatch(clearData([entities.vendor, entities.adminShop])),
  clearError: () =>
    dispatch(clearError([entities.vendor, entities.adminShop, entities.vendorReturns])),
  getListingData: ({ vendorReturnID, page, sort, filter }) =>
    dispatch(getData(endpoints.vendorReturnItem.list({ vendorReturnID, page, sort, filter }))),
});

const formSettings = {
  enableReinitialize: true,
  mapPropsToValues: ({ vendorID, shopID }) => ({
    vendorID,
    shopID: shopID || window.merchantos.session.getShop().shopID,
  }),
  handleSubmit: async (
    values,
    {
      props: {
        postDataVendorReturns,
        putDataVendorReturns,
        getListingData,
        isEditMode,
        isCreateMode,
        vendorReturnID,
        handleCloseModal,
      },
      setSubmitting,
      setErrors,
    }
  ) => {
    try {
      setSubmitting(true);

      if (isCreateMode) {
        await postDataVendorReturns({
          payload: {
            vendorReturn: {
              vendorID: values.vendorID,
              shopID: values.shopID,
            },
          },
          onSuccess: ({ data: { vendorReturnID: id } = {} }) => {
            handleCloseModal();
            window.PageRouter.goTo(VENDOR_RETURN_PATH.replace(':id', id));
          },
        });
      } else if (isEditMode) {
        await putDataVendorReturns({
          id: vendorReturnID,
          payload: {
            vendorReturn: {
              vendorID: values.vendorID,
              shopID: values.shopID,
            },
          },
          onSuccess: () => {
            handleCloseModal();
            window.flashNotifier('success', 'Vendor return updated.', 3);
            getListingData({
              vendorReturnID,
              ...qs.parse(window.location.search, { ignoreQueryPrefix: true }),
            });
          },
        });
      }
    } catch (e) {
      setSubmitting(false);
      setErrors(e.errors);
    }
  },
};

const ConnectedVendorReturnModal = connect(
  mapStateToProps,
  mapDispatchToProps
)(withFormik(formSettings)(VendorReturnModal));

ConnectedVendorReturnModal.propTypes = {
  vendorID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  shopID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

ConnectedVendorReturnModal.defaultProps = {
  vendorID: '',
  shopID: '',
};

export default ConnectedVendorReturnModal;
